import React from 'react';
import { Link } from 'react-router-dom';
import {
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  EnvelopeIcon,
  NewspaperIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import Navbar from '../Components/Navbar';
import HeroSection from '../Components/HeroSection';
import Footer from '../Components/Footer';
import { articles, ArticleCard } from '../Components/BlogShared';

const mediaTopics = [
  {
    title: 'Insurance Education',
    description:
      'Clear, practical explanations that help families and business owners understand coverage and make informed decisions.',
    icon: ShieldCheckIcon,
  },
  {
    title: 'Community Stories',
    description:
      'Stories about the people, families, and communities we are proud to serve throughout Minneapolis and beyond.',
    icon: UserGroupIcon,
  },
  {
    title: 'Agency Updates',
    description:
      'News from Birimo Insurance Agency, including team updates, services, partnerships, and local events.',
    icon: BuildingOffice2Icon,
  },
];

const Media = () => {
  return (
    <>
      <Navbar />
      <HeroSection />

      <main className="bg-slate-50 text-slate-900">
        <section className="relative overflow-hidden bg-navcolor px-6 py-20 text-white md:px-8">
          <div className="absolute -left-20 top-8 h-64 w-64 rounded-full bg-cyan-300/20 blur-3xl" />
          <div className="absolute -right-16 bottom-0 h-72 w-72 rounded-full bg-violet-300/20 blur-3xl" />

          <div className="relative mx-auto max-w-6xl">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15">
              <NewspaperIcon className="h-8 w-8" />
            </div>
            <p className="mt-7 text-sm font-semibold uppercase tracking-[0.35em] text-blue-100">
              Birimo Media Center
            </p>
            <h1 className="mt-4 max-w-4xl font-serif text-4xl font-bold leading-tight md:text-6xl">
              Insurance insight rooted in our community
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-blue-50">
              Explore agency updates, educational resources, and stories from the people behind Birimo Insurance Agency.
            </p>
          </div>
        </section>

        <section className="border-b border-slate-200 bg-[#fbfaf7] px-6 py-16 md:px-8">
          <div className="mx-auto max-w-6xl">
            <div className="mb-9 flex flex-col justify-between gap-5 md:flex-row md:items-end">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-navcolor">The Birimo Journal</p>
                <h2 className="mt-3 font-serif text-3xl font-bold md:text-4xl">Life insurance, explained clearly.</h2>
                <p className="mt-4 max-w-xl leading-7 text-slate-600">Ten practical guides for your family, available in English and Spanish.</p>
              </div>
              <Link to="/Blog" className="w-fit shrink-0 rounded-full bg-navcolor px-6 py-3 text-sm font-semibold text-white transition hover:bg-indigo-700">Explore all articles →</Link>
            </div>
            <div className="grid gap-6 md:grid-cols-3">
              {articles.slice(0, 3).map((article) => <ArticleCard key={article.slug} article={article} />)}
            </div>
            <p className="mt-7 text-center text-sm text-slate-600" lang="es">¿Prefieres leer en español? <Link to="/Blog?lang=es" className="font-semibold text-navcolor underline underline-offset-4">Ver las guías en español →</Link></p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-6 py-16 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.3em] text-navcolor">
              What We Share
            </p>
            <h2 className="mt-3 font-serif text-3xl font-bold md:text-4xl">
              Helpful information, local perspective
            </h2>
            <p className="mt-4 text-base leading-7 text-slate-600">
              Our media center is a home for useful insurance information and the latest stories from our agency.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {mediaTopics.map((topic) => (
              <article
                key={topic.title}
                className="rounded-[24px] border border-slate-200 bg-white p-7 shadow-sm transition duration-300 hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-navcolor">
                  <topic.icon className="h-6 w-6" />
                </div>
                <h3 className="mt-6 text-xl font-bold">{topic.title}</h3>
                <p className="mt-3 leading-7 text-slate-600">{topic.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-white px-6 py-16 md:px-8">
          <div className="mx-auto grid max-w-6xl overflow-hidden rounded-[30px] border border-slate-200 bg-slate-100 shadow-lg md:grid-cols-2">
            <div className="min-h-[360px]">
              <img
                src="/images/abshir.jpg"
                alt="Abshir Mohamed of Birimo Insurance Agency"
                className="h-full w-full object-cover"
                style={{ objectPosition: 'center' }}
              />
            </div>
            <div className="flex flex-col justify-center p-8 md:p-12">
              <p className="text-sm font-bold uppercase tracking-[0.3em] text-navcolor">
                Our People
              </p>
              <h2 className="mt-3 font-serif text-3xl font-bold">
                Local expertise in action
              </h2>
              <p className="mt-5 leading-7 text-slate-600">
                Our team combines insurance knowledge, multilingual service, and personal attention to make coverage conversations clearer and more welcoming.
              </p>
              <a
                href="/ContactUS"
                className="mt-7 inline-flex w-fit rounded-full bg-navcolor px-6 py-3 font-semibold text-white transition hover:bg-blue-700"
              >
                Meet Our Team
              </a>
            </div>
          </div>
        </section>

        <section className="px-6 py-16 md:px-8">
          <div className="mx-auto max-w-6xl rounded-[30px] bg-slate-900 px-7 py-10 text-white md:px-12 md:py-14">
            <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
              <div>
                <div className="flex items-center gap-3 text-blue-200">
                  <ChatBubbleLeftRightIcon className="h-6 w-6" />
                  <p className="text-sm font-bold uppercase tracking-[0.28em]">
                    Media Inquiries
                  </p>
                </div>
                <h2 className="mt-4 font-serif text-3xl font-bold md:text-4xl">
                  Looking for an insurance perspective?
                </h2>
                <p className="mt-4 max-w-2xl leading-7 text-slate-300">
                  Contact our team for agency information, interview requests, community stories, or insurance-related commentary.
                </p>
              </div>
              <a
                href="mailto:info@birimoinsurance.com"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 font-semibold text-navcolor transition hover:bg-blue-50"
              >
                <EnvelopeIcon className="h-5 w-5" />
                Contact Our Team
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Media;
